import { SCORE_FACTOR_DESCRIPTIONS } from "../../../../requests/ExperianLabel";

interface ConsumerIdentity {
  name?: Array<{
    firstName?: string;
    middleName?: string;
    generationCode?: string;
    surname?: string;
  }>;
  dob?: {
    month?: string;
    day?: string;
    year?: string;
  };
}

interface Address {
  streetPrefix?: string;
  streetName?: string;
  streetSuffix?: string;
  city?: string;
  state?: string;
  zipCode?: string;
}

interface Employment {
  name?: string;
  firstReportedDate?: string;
}

interface InformationalMessage {
  messageNumberDetailed?: string;
  messageText?: string;
}

interface Attribute {
  id?: string;
  value?: string;
}

interface Summary {
  attributes?: Attribute[];
}

interface RiskModel {
  modelIndicator?: string;
  score?: string;
  scoreFactors?: Array<{
    code?: string;
  }>;
}

interface Tradeline {
  subscriberName?: string;
  subscriberCode?: string;
  openDate?: string;
  enhancedPaymentData?: {
    originalLoanAmount?: string;
  };
  statusDate?: string;
  lastPaymentDate?: string;
  balanceDate?: string;
  balanceAmount?: string;
  openOrClosed?: string;
  accountNumber?: string;
  accountType?: string;
  revolvingOrInstallment?: string;
  paymentHistory?: string;
  delinquencies30Days?: string;
  delinquencies60Days?: string;
  delinquencies90to180Days?: string;
  derogCounter?: string;
  maxDelinquencyDate?: string;
  monthsHistory?: string;
}

interface Inquiry {
  date?: string;
  subscriberName?: string;
  subscriberCode?: string;
  amount?: string;
  type?: string;
  terms?: string;
}

interface CreditProfile {
  consumerIdentity?: ConsumerIdentity;
  ssn?: Array<{
    number?: string;
  }>;
  addressInformation?: Address[];
  employmentInformation?: Employment[];
  informationalMessage?: InformationalMessage[];
  summaries?: Summary[];
  riskModel?: RiskModel[];
  tradeline?: Tradeline[];
  inquiry?: Inquiry[];
}

interface ParsedJSON {
  creditProfile?: CreditProfile[];
}

class ReportGenerator {
  static generateHTMLFromJSON(parsedJSON: ParsedJSON): string {
    console.log("Starting report generation with JSON:", JSON.stringify(parsedJSON, null, 2));
    try {
      const html: string[] = [];
      const styles = `
        <style>
            body {
                font-family: Arial, sans-serif;
                padding: 6px;
                font-size: 14px;
                margin: 20px auto;  
                max-width: 800px;   
            }
            .header {
                font-size: 13px;
            }
            .header-title { 
               font-size: 25px;
               font-weight: bold;
               color: blue;
            }
            .header-subtitle {
                font-size: 20px;
                color: #d55252;
                margin-bottom: 1px;
                margin-top: 4px;
                margin-left: 2px;
            }
            .line { 
                width: 100%;
                height: 1px;
                background-color: #ccc;
                margin-bottom: 10px; 
             }
            .current-date {
                font-size: 12px;
                margin-bottom: -14px;
                margin-left: 3px;
           }
            .section {
                margin-top: 24px; 
            }           
            .section-title {
                font-size: 20px;
                font-weight: bold;
                color: #0e0b05;
                background-color: #c7cdcd;
                padding: 8px;
                padding-left: 4px;
            }       
            .section-title-gap { 
                margin-top: 20px; 
                font-size: 18px;
                font-weight: bold;
                color: #333;
            } 
            .info-row {
                display: flex;
                justify-content: space-between;
            }
            .info-item {
                flex: 1;
                padding: 5px;
            }
            .message-row {
                display: flex;
                margin-left: 10px;
                margin-right: 20%;
                gap: 23px;
            }
            .message-row-header {
                font-weight: bold;
                border-bottom: 1px solid #ccc;
                padding-bottom: 3px;
                margin-bottom: 7px;
                margin-top: 3px;
                display: flex;
                margin-left: 10px;
            }
            .message-type {
                width: 19%;
                padding-right: 10px;
            }
            .message-content {
                margin-left: -10px;
            }
            .info-personal {
                margin-left: 15px; 
                display: flex; 
                justify-content: space-between;
                margin-right: 20%; 
            }
            .grey-row { 
                background-color: #f0f0f0; 
                padding: 10px; 
                font-weight: bold; 
                display: inline-block; 
                width: 180px;
            }
            .name-value {
                display: inline-block;
                font-weight: normal;
            }
            .dob-ssn-container { 
                display: flex; 
                justify-content: space-between; 
                width: 100%;
            }
            .dob-ssn-container div { 
                width: 48%;
            }
           .address-title {
                font-weight: bold;
                padding: 5px;
                padding-bottom: 2px;
           }
            .address-value {
                margin-left: 5px; 
            }
            .employment-title { 
                font-weight: bold; 
                padding: 5px; 
                padding-bottom: 2px;
             }
            .employment-value { 
                margin-left: 5px;
            }          
            .inquiry-row {
                display: flex;
                justify-content: flex-start;
                align-items: center;
                margin-bottom: 10px;
            }
            .inquiry-type, .inquiry-content {
                width: 14%; 
                text-align: left;
                padding: 5px;
            }
            .inquiry-row div {
                overflow-wrap: break-word; 
            }
            .inquiry-row .inquiry-type, .inquiry-row .inquiry-content {
                word-wrap: break-word;
            }
            .summary-item {
                flex: 1; 
                padding: 5px;
                margin: 0 10px;
                text-align: left;
            }
            .profile-summary-row {
                display: grid;
                grid-template-columns: 0.9fr 0.9fr 0.9fr;
                gap: 10px;
                margin-bottom: 8px;
                margin-top: 10px;
                font-size: 14px;
                margin-left: -11px;
            }
            .summary-item {
                padding: 5px;
                display: flex;
                justify-content: space-between; 
            }
            .score-summary-row {
                margin-bottom: -2px;
                display: flex;
            }           
            .score-summary-row.header {
                border-bottom: 1px solid #e1cfcf;
            }
            .score-summary-item {
               padding: 6px 5px;
               text-align: left;
               width: 8%;
               font-size: 14px;
            }
            .score-summary-item-scorefactors {
                margin-left: 8px;
                margin-top: 6px;
                font-size: 14px;
            }
            .score-summary-item-description {
               margin-left: 87px;
               margin-top: 6px;
               font-size: 14px;
               font-family: serif;
            }
            .creditor-detail-row {
                display: grid;
                grid-template-columns: repeat(6, 0.9fr);
                gap: 19px;
                text-align: center;
                margin-bottom: 15px;
                margin-left: -19px;
                margin-top: 10px;
            }
            .creditor-detail-item {
                font-size: 13px;
                font-weight: bold;
                border-bottom: 1px solid #f3eaea;
            }
            .creditor-detail-value {
                font-size: 12px;
                font-weight: normal;
                color: #333;
                margin-top: -5px;
            }
            .creditor-account-row {
                display: flex;
                margin-left: 10px;
            }
            .creditor-account-item {
                flex: 1;
            }
            .creditor-name-row {
                margin-top: 6px;
            }
            .creditor-name-item {
                font-size: 15px;
                color: #131e2c;
                background-color: #eaecef;
                padding: 9px;
                padding-left: 3px;
            }
            .creditor-info-row {
                display: flex;
                justify-content: space-between;
                margin-top: 5px;
                gap: 20px;
            }
            .creditor-payment-row {
               display: flex;
               margin-left: 10px;
               align-items: center;
               margin-top: 5px;
               font-size: 12px;
            }
            .creditor-payment-item {
                width: 78px;
            }
            .creditor-payment-months {
                  font-size: 14px;
                  margin-top: -1px;
                  padding: 3px;
                  margin-left: -12px;
                  border-bottom: 1px solid rgb(204, 204, 204);
            }
            .creditor-payment-values {
               font-size: 13px;
               padding: 3px;
               margin-left: -8px;
               grid-template-columns: repeat(12, 1fr);
               gap: 17.5px;
               display: grid;
               background-color: rgb(249, 249, 249);
            }
            .creditor-delinquency-counter {
               padding: 10px;
            }
            .creditor-delinquency-row {
                display: flex;
                margin-top: 5px;
            }
            .creditor-delinquency-header {
                font-size: 13px;
                gap: 16px;
                margin-right: 21px;
                margin-left: 13px;
                display: flex;
                border-bottom: 1px solid rgb(204, 204, 204);
            }
            .creditor-delinquency-values {
                font-size: 12px;
                padding: 4px;
                margin-top: 9px;
                margin-left: 156px;
                gap: 19px;
                display: flex;
                background-color: rgb(249, 249, 249);  
            }
            .delinquency-info-item {         
                margin-left: 73.5%;
                padding: 3px;
            }
            .inquiry-row-header {
                 font-size: 14px;
                 display: flex;
                 font-weight: bold;
                 border-bottom: 1px solid rgb(204, 204, 204);
            }
            .inquiry-row .inquiry-type, .inquiry-row .inquiry-content {
                word-wrap: break-word;
                font-size: 13px;
            }
            .inquiry-type, .inquiry-content {
                width: 13%;
                text-align: left;
                padding: 5px;
            }
            .final-section {
                margin-top: 13px;
                padding: 10px;
                border-top: 2px solid #ccc;
                text-align: left;
            }
            .final-title {
                font-size: 14px;
                margin-bottom: 3px;
                margin-left: -7px;
            }
            .consumer-assistance p {
                margin: 0;
                margin-left: -7px;
                font-weight: bold;
            }
        </style>
        `;
      html.push(styles);

      html.push(`
            <div class="header">
                <div class="header-title">Credit Profile Report</div>
                <div class="header-subtitle">Applicant</div>
                <div class="line"></div>
                <div class="current-date" id="current-date">${formatDateWithTime(new Date())}</div>
            </div>
        `);

      const profile = parsedJSON.creditProfile?.[0] ?? {};
      const consumer = profile.consumerIdentity ?? {};

      let nameValue = "";
      if (consumer?.name?.[0]) {
        const firstName = consumer.name[0]?.firstName ?? "";
        const surname = consumer.name[0]?.surname ?? "";
        nameValue = `${firstName} ${surname}`.trim();
      }

      let otherNames = "";
      if (Array.isArray(consumer?.name) && consumer.name.length > 1) {
        for (let i = 1; i < consumer.name.length; i++) {
          const nameEntry = consumer.name[i] || {};
          let fullName = nameEntry?.firstName ?? "";
          if (nameEntry?.middleName) fullName += ` ${nameEntry.middleName}`;
          if (nameEntry?.generationCode) fullName += ` ${nameEntry.generationCode}`;
          fullName += ` ${nameEntry?.surname ?? ""}`;
          otherNames += (otherNames ? ", " : "") + fullName.trim();
        }
      }

      let dobDisplay = "N/A";
      if (consumer?.dob) {
        const { month, day, year } = consumer.dob;
        dobDisplay = `${month ?? ""}/${day ?? ""}/${year ?? ""}`.replace(/^\/|\/$/g, "");
        if (dobDisplay === "//") dobDisplay = "N/A";
      }

      const addresses = profile.addressInformation ?? [];
      const employment = profile.employmentInformation ?? [];

      html.push(`
    <div class="section">
        <div class="section-title">Personal Information</div>
        <div class="info-row">
            <div class="info-item">
                <div><strong>Name</strong></div>
                <div class="name-value">${nameValue}</div>
            </div>
            <div class="info-item">
                <div><strong>Other Names</strong></div>
                <div>${otherNames}</div>
            </div>
        </div>

        <div class="info-row">
            <div class="info-item">
                <div><strong>Date of Birth</strong></div>
               <div>${
                 consumer?.dob
                   ? `${consumer.dob.month ?? "N/A"}/${consumer.dob.day ?? "N/A"}/${
                       consumer.dob.year ?? "N/A"
                     }`
                   : "N/A"
               }</div>                      
            </div>
            <div class="info-item">
                <div><strong>Social Security Number</strong></div>
                <div>${formatSSN(profile?.ssn?.[0]?.number ?? "")}</div>
            </div>
        </div>

        <div class="info-address" id="addresses">
            ${
              addresses.length > 0
                ? `
                <div class = address-title>Address</></div>
                <div class="address-value">
               ${addresses[0]?.streetPrefix ?? ""} ${addresses[0]?.streetName ?? ""} ${
                    addresses[0]?.streetSuffix ?? ""
                  }, 
${addresses[0]?.city ?? ""}, ${addresses[0]?.state ?? ""} ${addresses[0]?.zipCode ?? ""}
              </div>
                ${
                  addresses.length > 1
                    ? `
                    <div class="address-title other-addresses">Other Addresses</div>
                    ${addresses
                      .slice(1)
                      .map(
                        (address) => `
                        <div class="address-value">${address?.streetPrefix} ${address?.streetName} ${address?.streetSuffix}, ${address?.city}, ${address?.state} ${address?.zipCode}</div>
                    `
                      )
                      .join("")}
                `
                    : ""
                }
            `
                : ""
            }
        </div>

        <div class="info-employer" id="employment">
            ${
              employment.length > 0
                ? `
                <div class="employment-title employer">Employer</div>
               <div class="employment-value">${employment[0]?.name ?? "N/A"} (First Reported: ${
                    employment[0]?.firstReportedDate ?? "N/A"
                  })</div>
                ${
                  employment.length > 1
                    ? `
                    <div class="employment-title other-employers">Other Employers</div>
                    ${employment
                      .slice(1)
                      .map(
                        (job) => `
                        <div class="employment-value">${job?.name} (First Reported: ${job?.firstReportedDate})</div>
                    `
                      )
                      .join("")}
                `
                    : ""
                }
            `
                : ""
            }
        </div>

    </div>
`);

      const messages = profile.informationalMessage ?? [];
      if (messages.length > 0) {
        html.push(`
                 <div class="section">
            <div class="section-title">Informational Messages</div>
            <div class="message-row-header">
                <div class="message-type"><strong>Type</strong></div>
                <div class="message-content"><strong>Message</strong></div>
            </div>
    `);
        messages.forEach((message) => {
          html.push(`
                    <div class="message-row">
                <div class="message-type">${message?.messageNumberDetailed ?? ""}</div>
                <div class="message-content">${message?.messageText ?? ""}</div>
            </div>
                `);
        });

        html.push(`</div>`);
      }

      const profileSummary = profile.summaries ?? [];
      if (profileSummary.length > 0) {
        const summary = profileSummary[0];
        html.push(`
        <div class="section">
            <div class="section-title">Profile Summary</div>
      
    `);
        const attributes = summary?.attributes || [];
        let row: string[] = [];
        attributes.forEach((attribute, index) => {
          row.push(`
              <div class="summary-item">
                <span>${attribute?.id ?? ""}:</span>
                <span>${this.getValue(attribute?.value ?? "")}</span>
            </div>
        `);

          if ((index + 1) % 3 === 0 || index === attributes.length - 1) {
            html.push(`
                <div class="profile-summary-row">${row.join("")}</div>
            `);
            row = [];
          }
        });
        html.push(`</div>`);
      }

      const riskModel = profile.riskModel ?? [];
      console.log("Risk models found:", riskModel);
      const validRiskModels = riskModel.filter((model) => model != null);
      console.log("Valid risk models:", validRiskModels);

      if (validRiskModels.length > 0) {
        html.push(`
        <div class="section">
            <div class="section-title">Score Summary</div>
            <div class="score-summary-row header">
                <div class="score-summary-item"><strong>Risk Model</strong></div>
                <div class="score-summary-item"><strong>Score</strong></div>
                <div class="score-summary-item"><strong>Code</strong></div>
                <div class="score-summary-item"><strong>Score Factor Description</strong></div>
            </div>
    `);

        validRiskModels.forEach((model) => {
          // Safely get scoreFactors - check if property exists and is an array
          const factors =
            model.scoreFactors && Array.isArray(model.scoreFactors) ? model.scoreFactors : [];
          const validFactors = factors.filter((factor) => factor != null && factor.code != null);
          html.push(`
            <div class="score-summary-row">
                <div class="score-summary-item">${model?.modelIndicator ?? ""}</div>
                <div class="score-summary-item">${model?.score ?? ""}</div>
                <div class="score-summary-item-scorefactors">
                       ${validFactors.map((factor) => `<div>${factor.code}</div>`).join("")}
            </div>
             <div class="score-summary-item-description">
               ${validFactors
                 .map((factor) => {
                   const code = factor.code.toString();
                   const description =
                     SCORE_FACTOR_DESCRIPTIONS[code as keyof typeof SCORE_FACTOR_DESCRIPTIONS];
                   return `<div>${description || "Description not available"}</div>`;
                 })
                 .join("")}
            </div>   
            </div>
        `);
        });
        html.push(`</div>`);
      }

      const tradeline = profile.tradeline ?? [];
      if (tradeline.length > 0) {
        html.push(`
             <div class="section">
            <div class="section-title">Creditors</div>
            `);
        tradeline.forEach((line) => {
          html.push(`
         <div class="creditor-name-row">
                <div class="creditor-name-item">
                    <strong>${line?.subscriberName ?? "N/A"}</strong>
                    <small>(${line?.subscriberCode ?? "N/A"})</small>
                </div>
            </div>

         <div class="creditor-detail-row header">
        <div class="creditor-detail-item">Open Date</div>
        <div class="creditor-detail-item">Original Amount</div>
        <div class="creditor-detail-item">Status Date</div>
        <div class="creditor-detail-item">Past Paid Date</div>
        <div class="creditor-detail-item">Balance Date</div>
        <div class="creditor-detail-item">Current Balance</div>
    </div>
    <div class="creditor-detail-row">
        <div class="creditor-detail-value">${formatDate(line?.openDate ?? "")}</div>
        <div class="creditor-detail-value">
           ${formatAmount(line?.enhancedPaymentData?.originalLoanAmount ?? "")}
        </div>
        <div class="creditor-detail-value">${formatDate(line?.statusDate ?? "")}</div>
        <div class="creditor-detail-value">${formatDate(line?.lastPaymentDate ?? "")}</div>
        <div class="creditor-detail-value">${formatDate(line?.balanceDate ?? "")}</div>
        
      
        <div class="creditor-detail-value">${formatAmount(line?.balanceAmount ?? "")}</div>
    </div>
 
    <div class="creditor-account-row">
        <div class="creditor-account-item">
            <strong>Account Condition:</strong> ${
              line?.openOrClosed === "C" ? "Closed" : line?.openOrClosed === "O" ? "Open" : "N/A"
            }
        </div>
        <div class="creditor-account-item">
            <strong>Account#:</strong> ${line?.accountNumber || "N/A"}
        </div>
    </div>
    
    <div class="creditor-account-row">
        <div class="creditor-account-item">
            <strong>Account Type:</strong> ${line?.accountType || "N/A"}
        </div>
        <div class="creditor-account-item">
            <strong>Account Terms:</strong> ${
              line?.revolvingOrInstallment === "R"
                ? "Revolving"
                : line?.revolvingOrInstallment === "I"
                ? "Installment"
                : "N/A"
            }
        </div>
    </div>
    
      `);
          if (line?.balanceDate && line?.paymentHistory) {
            const months = [
              "Jan",
              "Feb",
              "Mar",
              "Apr",
              "May",
              "Jun",
              "Jul",
              "Aug",
              "Sep",
              "Oct",
              "Nov",
              "Dec",
            ];

            const history = line?.paymentHistory?.split("")?.reverse() ?? [];
            const lastPayment = line?.balanceDate ?? "";
            const lastPaymentYear = parseInt(lastPayment.slice(4), 10); // Extract year
            const lastPaymentMonth = parseInt(lastPayment.slice(0, 2), 10) - 1; // Month is 0-indexed

            let year = lastPaymentYear;
            let month = lastPaymentMonth;

            const paymentRows: { [key: number]: string[] } = {};
            history.forEach((status) => {
              if (!paymentRows[year]) paymentRows[year] = new Array(12).fill("");
              paymentRows[year][month] = status;
              month--;
              if (month < 0) {
                month = 11;
                year--;
              }
            });

            html.push(`
                <div class="creditor-info-row">
                    <div class="creditor-payment-history">
                      <div class="creditor-payment-row"> 
                        <div class="creditor-payment-item">
                            <strong>Payment History:</strong>
                        </div>
                        <div class="creditor-payment-months">
                            ${months.join(" ")}
                        </div>
                        </div>
            `);
            Object.keys(paymentRows)
              .sort((a, b) => Number(b) - Number(a))
              .forEach((year) => {
                html.push(`
                 <div class="creditor-payment-row">
                    <div class="creditor-payment-item">
                     <strong>${year}:</strong>
                 </div>
                 <div class="creditor-payment-values">
              ${paymentRows[Number(year)]
                .map(
                  (status: string) =>
                    `<span class="${status ? "" : "empty"}">${status || ""}</span>`
                )
                .join("")}
                </div>
                </div>
            `);
              });
            html.push(`
                    </div>
                    
                    <div class="creditor-delinquency-counter">
                    <div class="creditor-delinquency-row">
                        <div class="creditor-delinquency-item">
                            <strong>Delinquency Counter:</strong>
                        </div>
                        <div class="creditor-delinquency-header">
                           <div>60</div>
                            <div>90</div>
                            <div>90+</div>
                            <div>Derog</div>
                        </div>
                        </div>
                        <div class="creditor-delinquency-values">
                            <div>${line?.delinquencies30Days ?? "N/A"}</div>
                            <div>${line?.delinquencies60Days ?? "N/A"}</div>
                            <div>${line?.delinquencies90to180Days ?? "N/A"}</div>
                            <div>${line?.derogCounter ?? "N/A"}</div>
                        </div>
                    </div>
                </div>
               
            <div class="delinquency-info-row">
                <div class="delinquency-info-item">
                    <strong>Worst Deliquency Date:</strong> ${line?.maxDelinquencyDate ?? "N/A"}
                </div>
                <div class="delinquency-info-item">
                    <strong>Months Reviewed:</strong> ${line?.monthsHistory ?? "N/A"}
                </div>
            </div>
            `);
          }
        });
        html.push(`</div>`);
      }

      const inquiries = profile.inquiry ?? [];
      if (inquiries.length > 0) {
        html.push(`
            <div class = "section">
            <div class="section-title">Inquiry</div>
            <div class = "inquiry-row-header">
            <div class="inquiry-content">Date</div>
             <div class="inquiry-type">Subscriber</div>
               <div class="inquiry-content">Amount</div>
                <div class="inquiry-content">Type</div>
                <div class="inquiry-content">Terms</div>                                  
             </div>
        `);

        inquiries.forEach((inquiry) => {
          html.push(`
                <div class="inquiry-row">
                <div class="inquiry-content">${formatDate(inquiry.date)}</div>
                <div class="inquiry-content">
                    <strong>${inquiry?.subscriberName ?? "N/A"}</strong>
                    <small>${inquiry?.subscriberCode ?? "N/A"}</small>
                </div>
               <div class="inquiry-content">${inquiry?.amount ?? "N/A"}</div> 
               <div class="inquiry-content">${inquiry?.type ?? "N/A"}</div>
               <div class="inquiry-content">${inquiry?.terms ?? "N/A"}</div>                     
            </div>
                `);
        });
        html.push(`</div>`);
      }

      html.push(`
    <div class="final-section">
        <div class="final-title">END - Experian Credit Profile Report</div>
        <div class="consumer-assistance">
            <p><strong>Consumer Assistance</strong></p>
            <p>Experian</p>
            <p>701 Experian Parkway</p>
            <p>P.O. Box</p>
            <p>Allen, TX 75013-0036</p>
            <p>(888) 397-3742</p>
            <p><a href="https://www.experian.com/reportaccess" target="_blank">www.experian.com/reportaccess</a></p>
        </div>
    </div>
`);

      html.push(`</div></div>`);
      return html.join("");
    } catch (error) {
      console.error("Error generating HTML report:", error);
      const errorMessage = error instanceof Error ? error.message : String(error);
      return `<div style="color: red; padding: 20px; font-family: Arial, sans-serif;">
              <h3>Error Generating Credit Report</h3>
              <p><strong>Error:</strong> ${errorMessage}</p>
              <p>This may be due to unexpected data format in the credit report.</p>
              <p>Please contact support if this issue persists.</p>
            </div>`;
    }
  }

  static getValue(value?: string): string {
    return value && value.trim() !== "" ? value : "N/A";
  }
}

function getAttributeValue(summary: Summary, id: string): string | null {
  const attribute = summary.attributes.find((attr) => attr.id === id);
  return attribute ? attribute.value : null;
}

function formatDate(dateString: string): string {
  if (!dateString || dateString.length !== 8) return "N/A";
  const month = dateString.substring(0, 2);
  const day = dateString.substring(2, 4);
  const year = dateString.substring(4);
  return `${month}/${day}/${year}`;
}

function formatAmount(amount: string): string {
  if (!amount) return "N/A";
  const formattedAmount = Number(amount).toLocaleString();
  return formattedAmount;
}

function formatDateWithTime(dateString: Date): string {
  const date = new Date(dateString);
  const options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  };
  return date.toLocaleString("en-US", options);
}

function formatSSN(ssn: string): string {
  if (!ssn || ssn.length !== 9 || !/^\d+$/.test(ssn)) return "N/A";
  return `${ssn.slice(0, 3)}-${ssn.slice(3, 5)}-${ssn.slice(5)}`;
}

export default ReportGenerator;
